import React, { useState, Fragment } from 'react';
import { TruckIcon, CalendarIcon, CheckIcon, XIcon, InfoIcon, RefreshCwIcon, CheckCircleIcon, XCircleIcon, ChevronRightIcon, ChevronLeftIcon, UsersIcon, FilterIcon, BuildingIcon, HandshakeIcon } from 'lucide-react';
interface BusinessPartner {
  id: string;
  name: string;
  company: string;
  role: 'broker' | 'contractor';
  email: string;
  phone: string;
  status: 'active' | 'pending' | 'inactive';
  connectedSince: string;
}
interface Truck {
  id: string;
  truckNumber: string;
  type: string;
  status: string;
  make: string;
  model: string;
  assignedDriver?: string;
  availability?: {
    [date: string]: {
      available: boolean;
      partnerId?: string; // Partner ID that can access this truck on this date
    };
  };
}
interface TruckAvailabilityProps {
  trucks: Truck[];
  updateTruckAvailability: (truckId: string, dateAvailability: {
    [date: string]: {
      available: boolean;
      partnerId?: string;
    };
  }) => void;
  businessPartners?: BusinessPartner[];
}
export const TruckAvailability = ({
  trucks,
  updateTruckAvailability,
  businessPartners = []
}: TruckAvailabilityProps) => {
  const [selectedTruck, setSelectedTruck] = useState<string | null>(null);
  const [viewStartDate, setViewStartDate] = useState(new Date());
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [showPartnerSelector, setShowPartnerSelector] = useState<{
    truckId: string;
    date: string;
  } | null>(null);
  const [filterByPartner, setFilterByPartner] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'calendar' | 'partners'>('calendar');
  // Only show active business partners
  const activePartners = businessPartners.filter(partner => partner.status === 'active');
  // Generate the next 14 days for the calendar view
  const getDaysArray = (startDate: Date, numDays: number = 14) => {
    const days = [];
    const currentDate = new Date(startDate);
    for (let i = 0; i < numDays; i++) {
      days.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }
    return days;
  };
  const daysToShow = getDaysArray(viewStartDate);
  // Format date as YYYY-MM-DD for the availability object
  const formatDateKey = (date: Date) => {
    return date.toISOString().split('T')[0];
  };
  // Format date for display
  const formatDateDisplay = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };
  // Check if a date is today
  const isToday = (date: Date) => {
    const today = new Date();
    return date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
  };
  // Check if a date is tomorrow
  const isTomorrow = (date: Date) => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return date.getDate() === tomorrow.getDate() && date.getMonth() === tomorrow.getMonth() && date.getFullYear() === tomorrow.getFullYear();
  };
  // Get availability state for a truck and date
  const getTruckAvailability = (truck: Truck, date: Date) => {
    const dateKey = formatDateKey(date);
    if (!truck.availability || !truck.availability[dateKey]) {
      return {
        available: false,
        partnerId: undefined
      };
    }
    return truck.availability[dateKey];
  };
  // Get partner info for a truck and date
  const getPartnerInfo = (truck: Truck, date: Date) => {
    const availability = getTruckAvailability(truck, date);
    if (!availability.available || !availability.partnerId) {
      return null;
    }
    return activePartners.find(partner => partner.id === availability.partnerId) || null;
  };
  // Toggle availability for a specific truck and date
  const toggleAvailability = (truckId: string, date: Date) => {
    const dateKey = formatDateKey(date);
    const truck = trucks.find(t => t.id === truckId);
    if (!truck) return;
    const currentAvailability = getTruckAvailability(truck, date);
    const newAvailability = !currentAvailability.available;
    updateTruckAvailability(truckId, {
      [dateKey]: {
        available: newAvailability,
        partnerId: newAvailability ? currentAvailability.partnerId : undefined
      }
    });
  };
  // Set a specific partner for a truck and date
  const assignPartnerToTruck = (truckId: string, date: Date, partnerId: string | undefined) => {
    const dateKey = formatDateKey(date);
    const truck = trucks.find(t => t.id === truckId);
    if (!truck) return;
    updateTruckAvailability(truckId, {
      [dateKey]: {
        available: partnerId ? true : false,
        partnerId: partnerId
      }
    });
  };
  // Set availability for all days in the view for a specific truck
  const setAllDaysAvailability = (truckId: string, available: boolean, partnerId?: string) => {
    const truck = trucks.find(t => t.id === truckId);
    if (!truck) return;
    const updates: {
      [date: string]: {
        available: boolean;
        partnerId?: string;
      };
    } = {};
    daysToShow.forEach(date => {
      updates[formatDateKey(date)] = {
        available: available,
        partnerId: available ? partnerId : undefined
      };
    });
    updateTruckAvailability(truckId, updates);
  };
  // Set availability for weekdays or weekends for a specific truck
  const setWeekdayOrWeekendAvailability = (truckId: string, isWeekday: boolean, available: boolean, partnerId?: string) => {
    const truck = trucks.find(t => t.id === truckId);
    if (!truck) return;
    const updates: {
      [date: string]: {
        available: boolean;
        partnerId?: string;
      };
    } = {};
    daysToShow.forEach(date => {
      const day = date.getDay();
      // 0 is Sunday, 6 is Saturday
      const isWeekendDay = day === 0 || day === 6;
      if (isWeekday && !isWeekendDay || !isWeekday && isWeekendDay) {
        updates[formatDateKey(date)] = {
          available: available,
          partnerId: available ? partnerId : undefined
        };
      }
    });
    updateTruckAvailability(truckId, updates);
  };
  // Navigate to previous week
  const goToPreviousWeek = () => {
    const newStartDate = new Date(viewStartDate);
    newStartDate.setDate(newStartDate.getDate() - 7);
    setViewStartDate(newStartDate);
  };
  // Navigate to next week
  const goToNextWeek = () => {
    const newStartDate = new Date(viewStartDate);
    newStartDate.setDate(newStartDate.getDate() + 7);
    setViewStartDate(newStartDate);
  };
  // Reset to current week
  const goToCurrentWeek = () => {
    setViewStartDate(new Date());
  };
  return <div className="bg-white rounded-lg shadow p-4 lg:p-6" data-id="element-3953">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 lg:mb-6 gap-3" data-id="element-3954">
        <div data-id="element-3955">
          <h2 className="text-lg lg:text-xl font-bold" data-id="element-3956">Truck Availability</h2>
          <p className="text-sm text-gray-600 mt-1" data-id="element-3957">
            Manage which trucks are available for specific brokers and
            contractors
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2" data-id="element-3958">
          <div className="flex items-center" data-id="element-3959">
            <button onClick={() => setViewMode('calendar')} className={`px-3 py-1 text-sm rounded-l-md border ${viewMode === 'calendar' ? 'bg-blue-50 text-blue-700 border-blue-300' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`} data-id="element-3960">
              <CalendarIcon size={16} className="inline mr-1" data-id="element-3961" />
              Calendar View
            </button>
            <button onClick={() => setViewMode('partners')} className={`px-3 py-1 text-sm rounded-r-md border ${viewMode === 'partners' ? 'bg-blue-50 text-blue-700 border-blue-300' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`} data-id="element-3962">
              <UsersIcon size={16} className="inline mr-1" data-id="element-3963" />
              Partner View
            </button>
          </div>
          <button onClick={() => setShowBulkActions(!showBulkActions)} className="text-blue-600 hover:text-blue-800 flex items-center gap-1 text-sm" data-id="element-3964">
            <RefreshCwIcon size={16} data-id="element-3965" />
            {showBulkActions ? 'Hide Bulk Actions' : 'Show Bulk Actions'}
          </button>
          {viewMode === 'calendar' && <div className="flex items-center border rounded-md" data-id="element-3966">
              <button onClick={goToPreviousWeek} className="p-2 hover:bg-gray-100 border-r" data-id="element-3967">
                <ChevronLeftIcon size={16} data-id="element-3968" />
              </button>
              <button onClick={goToCurrentWeek} className="px-2 py-1 text-sm hover:bg-gray-100 border-r" data-id="element-3969">
                Today
              </button>
              <button onClick={goToNextWeek} className="p-2 hover:bg-gray-100" data-id="element-3970">
                <ChevronRightIcon size={16} data-id="element-3971" />
              </button>
            </div>}
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-6" data-id="element-3972">
        <div className="flex items-start gap-2" data-id="element-3973">
          <InfoIcon size={18} className="text-blue-500 mt-0.5 flex-shrink-0" data-id="element-3974" />
          <div className="text-sm text-blue-800" data-id="element-3975">
            <p className="font-medium" data-id="element-3976">
              How partner-specific availability works:
            </p>
            <p data-id="element-3977">
              1. Mark trucks as available and assign them to a specific business
              partner
            </p>
            <p data-id="element-3978">
              2. Business partners will only see trucks you've assigned to them
            </p>
            <p data-id="element-3979">
              3. Each truck can only be assigned to one partner on any given day
            </p>
          </div>
        </div>
      </div>

      {/* Partner filter (only in calendar view) */}
      {viewMode === 'calendar' && activePartners.length > 0 && <div className="mb-4 flex items-center gap-2" data-id="element-3980">
          <FilterIcon size={16} className="text-gray-500" data-id="element-3981" />
          <span className="text-sm font-medium" data-id="element-3982">Filter by partner:</span>
          <select value={filterByPartner || ''} onChange={e => setFilterByPartner(e.target.value || null)} className="border border-gray-300 rounded-md p-1 text-sm" data-id="element-3983">
            <option value="" data-id="element-3984">All partners</option>
            {activePartners.map(partner => <option key={partner.id} value={partner.id} data-id="element-3985">
                {partner.company} ({partner.role})
              </option>)}
          </select>
        </div>}

      {/* Calendar View */}
      {viewMode === 'calendar' && <div className="overflow-x-auto pb-2" data-id="element-3986">
          <table className="w-full min-w-max" data-id="element-3987">
            <thead data-id="element-3988">
              <tr className="bg-gray-50" data-id="element-3989">
                <th className="px-4 py-3 text-left font-medium text-gray-600 sticky left-0 bg-gray-50 z-10 min-w-[180px]" data-id="element-3990">
                  Truck
                </th>
                {daysToShow.map((date, index) => <th key={index} className={`px-2 py-3 text-center font-medium text-xs min-w-[100px] ${isToday(date) ? 'bg-blue-50 text-blue-800' : isTomorrow(date) ? 'bg-green-50 text-green-800' : 'text-gray-600'}`} data-id="element-3991">
                    <div className="flex flex-col items-center" data-id="element-3992">
                      <span className="uppercase" data-id="element-3993">
                        {date.toLocaleDateString('en-US', {
                    weekday: 'short'
                  })}
                      </span>
                      <span data-id="element-3994">
                        {date.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric'
                  })}
                      </span>
                      {isToday(date) && <span className="bg-blue-100 text-blue-800 text-xs px-2 rounded-full mt-1" data-id="element-3995">
                          Today
                        </span>}
                      {isTomorrow(date) && <span className="bg-green-100 text-green-800 text-xs px-2 rounded-full mt-1" data-id="element-3996">
                          Tomorrow
                        </span>}
                    </div>
                  </th>)}
              </tr>
            </thead>
            <tbody className="divide-y" data-id="element-3997">
              {trucks.map(truck => <Fragment key={truck.id} data-id="element-3998">
                  <tr className="hover:bg-gray-50" data-id="element-3999">
                    <td className="px-4 py-3 sticky left-0 bg-white hover:bg-gray-50 z-10" data-id="element-4000">
                      <div className="flex items-center gap-2" data-id="element-4001">
                        <TruckIcon size={18} className={truck.status === 'available' ? 'text-green-500' : truck.status === 'maintenance' ? 'text-orange-500' : 'text-blue-500'} data-id="element-4002" />
                        <div data-id="element-4003">
                          <div className="font-medium" data-id="element-4004">{truck.truckNumber}</div>
                          <div className="text-xs text-gray-500" data-id="element-4005">
                            {truck.type} • {truck.status}
                          </div>
                        </div>
                      </div>
                    </td>
                    {daysToShow.map((date, index) => {
                const dateKey = formatDateKey(date);
                const availability = getTruckAvailability(truck, date);
                const isAvailable = availability.available;
                const partner = getPartnerInfo(truck, date);
                // If filtering by partner, check if this truck is assigned to that partner
                const isVisibleWithFilter = !filterByPartner || isAvailable && availability.partnerId === filterByPartner;
                // If truck is in maintenance or assigned, it's not available regardless of settings
                const isDisabled = truck.status === 'maintenance' || truck.status === 'assigned';
                return <td key={index} className={`px-2 py-3 text-center ${isToday(date) ? 'bg-blue-50' : isTomorrow(date) ? 'bg-green-50' : ''} ${!isVisibleWithFilter && filterByPartner ? 'opacity-40' : ''}`} data-id="element-4006">
                          <div className="relative" data-id="element-4007">
                            <button onClick={() => toggleAvailability(truck.id, date)} disabled={isDisabled} className={`w-8 h-8 rounded-full mx-auto flex items-center justify-center ${isDisabled ? 'bg-gray-100 cursor-not-allowed' : isAvailable ? 'bg-green-100 hover:bg-green-200 text-green-700' : 'bg-red-100 hover:bg-red-200 text-red-700'}`} title={isDisabled ? `${truck.truckNumber} is ${truck.status}` : isAvailable ? `${truck.truckNumber} is available on ${formatDateDisplay(date)}` : `${truck.truckNumber} is not available on ${formatDateDisplay(date)}`} data-id="element-4008">
                              {isAvailable ? <CheckIcon size={16} data-id="element-4009" /> : <XIcon size={16} data-id="element-4010" />}
                            </button>
                            {isAvailable && !isDisabled && <div className="absolute -right-2 -bottom-2 bg-white rounded-full border border-gray-300 w-5 h-5 flex items-center justify-center text-xs hover:bg-gray-100 cursor-pointer" onClick={() => setShowPartnerSelector({
                      truckId: truck.id,
                      date: dateKey
                    })} title={partner ? `Assigned to ${partner.company}` : 'Click to assign to a partner'} data-id="element-4011">
                                {partner ? <div className={`w-3 h-3 rounded-full ${partner.role === 'broker' ? 'bg-purple-500' : 'bg-blue-500'}`} title={`${partner.company} (${partner.role})`} data-id="element-4012"></div> : <span data-id="element-4013">+</span>}
                              </div>}
                          </div>
                          {/* Partner selector dropdown */}
                          {showPartnerSelector && showPartnerSelector.truckId === truck.id && showPartnerSelector.date === dateKey && <div className="absolute z-20 mt-1 bg-white rounded-md shadow-lg border border-gray-200 p-2 min-w-[200px]" data-id="element-4014">
                                <div className="flex justify-between items-center mb-2 pb-1 border-b" data-id="element-4015">
                                  <span className="text-xs font-medium" data-id="element-4016">
                                    Assign to Partner
                                  </span>
                                  <button onClick={() => setShowPartnerSelector(null)} className="text-gray-500 hover:text-gray-700" data-id="element-4017">
                                    <XIcon size={14} data-id="element-4018" />
                                  </button>
                                </div>
                                {activePartners.length === 0 ? <div className="text-xs text-gray-500 py-1" data-id="element-4019">
                                    No active business partners
                                  </div> : <div className="space-y-1 max-h-[200px] overflow-y-auto" data-id="element-4020">
                                    {activePartners.map(partner => <div key={partner.id} className="flex items-center gap-2 text-xs p-1 hover:bg-gray-50 rounded cursor-pointer" onClick={() => {
                        assignPartnerToTruck(truck.id, date, availability.partnerId === partner.id ? undefined : partner.id);
                        setShowPartnerSelector(null);
                      }} data-id="element-4021">
                                        <div className="w-4 h-4 flex items-center justify-center" data-id="element-4022">
                                          {availability.partnerId === partner.id ? <div className="w-3 h-3 bg-blue-500 rounded-full" data-id="element-4023"></div> : <div className="w-3 h-3 border border-gray-300 rounded-full" data-id="element-4024"></div>}
                                        </div>
                                        <div data-id="element-4025">
                                          <div className="font-medium" data-id="element-4026">
                                            {partner.company}
                                          </div>
                                          <div className="text-gray-500 text-[10px]" data-id="element-4027">
                                            {partner.role}
                                          </div>
                                        </div>
                                      </div>)}
                                  </div>}
                                <div className="mt-2 pt-1 border-t" data-id="element-4028">
                                  <button onClick={() => {
                        assignPartnerToTruck(truck.id, date, undefined);
                        setShowPartnerSelector(null);
                      }} className="text-xs text-red-600 hover:text-red-800 w-full text-center py-1" data-id="element-4029">
                                    Clear Assignment
                                  </button>
                                </div>
                              </div>}
                        </td>;
              })}
                  </tr>
                  {showBulkActions && <tr className="bg-gray-50" data-id="element-4030">
                      <td className="px-4 py-2 sticky left-0 bg-gray-50 z-10 text-sm text-gray-500" data-id="element-4031">
                        Bulk actions
                      </td>
                      <td colSpan={daysToShow.length} className="px-4 py-2" data-id="element-4032">
                        <div className="flex flex-wrap gap-2" data-id="element-4033">
                          <button onClick={() => setAllDaysAvailability(truck.id, true)} disabled={truck.status !== 'available'} className="bg-green-100 hover:bg-green-200 text-green-800 px-2 py-1 rounded text-xs flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-4034">
                            <CheckCircleIcon size={12} data-id="element-4035" />
                            All Available
                          </button>
                          <button onClick={() => setAllDaysAvailability(truck.id, false)} disabled={truck.status !== 'available'} className="bg-red-100 hover:bg-red-200 text-red-800 px-2 py-1 rounded text-xs flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-4036">
                            <XCircleIcon size={12} data-id="element-4037" />
                            All Unavailable
                          </button>
                          {activePartners.length > 0 && <select onChange={e => {
                    if (e.target.value) {
                      setAllDaysAvailability(truck.id, true, e.target.value);
                    }
                  }} className="border border-gray-300 rounded text-xs p-1" disabled={truck.status !== 'available'} data-id="element-4038">
                              <option value="" data-id="element-4039">Assign all days to...</option>
                              {activePartners.map(partner => <option key={partner.id} value={partner.id} data-id="element-4040">
                                  {partner.company}
                                </option>)}
                            </select>}
                        </div>
                      </td>
                    </tr>}
                </Fragment>)}
            </tbody>
          </table>
        </div>}

      {/* Partner View */}
      {viewMode === 'partners' && <div className="space-y-6" data-id="element-4041">
          {activePartners.length === 0 ? <div className="text-center py-8 bg-gray-50 rounded-lg" data-id="element-4042">
              <HandshakeIcon size={40} className="mx-auto text-gray-400 mb-2" data-id="element-4043" />
              <p className="text-gray-600" data-id="element-4044">No active business partners</p>
              <p className="text-sm text-gray-500 mt-1" data-id="element-4045">
                Add partners in the Profile section to manage truck availability
              </p>
              <button onClick={() => setActiveTab('profile')} className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm" data-id="element-4046">
                Go to Profile
              </button>
            </div> : activePartners.map(partner => <div key={partner.id} className="border rounded-lg overflow-hidden" data-id="element-4047">
                <div className="bg-gray-50 p-3 border-b flex justify-between items-center" data-id="element-4048">
                  <div className="flex items-center gap-2" data-id="element-4049">
                    {partner.role === 'broker' ? <BuildingIcon size={18} className="text-purple-600" data-id="element-4050" /> : <HandshakeIcon size={18} className="text-blue-600" data-id="element-4051" />}
                    <div data-id="element-4052">
                      <h3 className="font-medium" data-id="element-4053">{partner.company}</h3>
                      <p className="text-xs text-gray-500" data-id="element-4054">
                        {partner.role === 'broker' ? 'Broker' : 'Contractor'} •{' '}
                        {partner.name}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-4055">
                    <span className="text-xs text-gray-500" data-id="element-4056">
                      Connected since{' '}
                      {new Date(partner.connectedSince).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <div className="p-4" data-id="element-4057">
                  <p className="text-sm text-gray-600 mb-3" data-id="element-4058">
                    Select which trucks are available to {partner.company} for
                    the upcoming days:
                  </p>
                  <div className="overflow-x-auto" data-id="element-4059">
                    <table className="w-full min-w-max" data-id="element-4060">
                      <thead data-id="element-4061">
                        <tr className="bg-gray-50" data-id="element-4062">
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-600" data-id="element-4063">
                            Truck
                          </th>
                          {daysToShow.slice(0, 7).map((date, index) => <th key={index} className={`px-2 py-2 text-center text-xs font-medium ${isToday(date) ? 'text-blue-800' : isTomorrow(date) ? 'text-green-800' : 'text-gray-600'}`} data-id="element-4064">
                              {date.toLocaleDateString('en-US', {
                      weekday: 'short',
                      month: 'numeric',
                      day: 'numeric'
                    })}
                            </th>)}
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-600" data-id="element-4065">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y" data-id="element-4066">
                        {trucks.map(truck => <tr key={truck.id} className="hover:bg-gray-50" data-id="element-4067">
                            <td className="px-3 py-2 text-sm" data-id="element-4068">
                              <div className="flex items-center gap-2" data-id="element-4069">
                                <TruckIcon size={16} className={truck.status === 'available' ? 'text-green-500' : truck.status === 'maintenance' ? 'text-orange-500' : 'text-blue-500'} data-id="element-4070" />
                                <div data-id="element-4071">
                                  <div className="font-medium" data-id="element-4072">
                                    {truck.truckNumber}
                                  </div>
                                  <div className="text-xs text-gray-500" data-id="element-4073">
                                    {truck.type}
                                  </div>
                                </div>
                              </div>
                            </td>
                            {daysToShow.slice(0, 7).map((date, index) => {
                    const availability = getTruckAvailability(truck, date);
                    const isAssignedToThisPartner = availability.available && availability.partnerId === partner.id;
                    const isAssignedToOtherPartner = availability.available && availability.partnerId && availability.partnerId !== partner.id;
                    const isDisabled = truck.status !== 'available' || isAssignedToOtherPartner;
                    return <td key={index} className="px-2 py-2 text-center" data-id="element-4074">
                                  <button onClick={() => {
                        if (isAssignedToThisPartner) {
                          assignPartnerToTruck(truck.id, date, undefined);
                        } else {
                          assignPartnerToTruck(truck.id, date, partner.id);
                        }
                      }} disabled={isDisabled} className={`w-6 h-6 rounded-full flex items-center justify-center ${isDisabled ? 'bg-gray-100 cursor-not-allowed' : isAssignedToThisPartner ? 'bg-green-100 hover:bg-green-200 text-green-700' : 'bg-red-100 hover:bg-red-200 text-red-700'}`} title={isDisabled ? isAssignedToOtherPartner ? 'Assigned to another partner' : `${truck.truckNumber} is ${truck.status}` : isAssignedToThisPartner ? `Remove ${truck.truckNumber} from ${partner.company}` : `Assign ${truck.truckNumber} to ${partner.company}`} data-id="element-4075">
                                    {isAssignedToThisPartner ? <CheckIcon size={14} data-id="element-4076" /> : <XIcon size={14} data-id="element-4077" />}
                                  </button>
                                </td>;
                  })}
                            <td className="px-3 py-2 text-right" data-id="element-4078">
                              <div className="flex justify-end gap-1" data-id="element-4079">
                                <button onClick={() => {
                        // Assign this truck to this partner for all visible days
                        const updates: {
                          [date: string]: {
                            available: boolean;
                            partnerId?: string;
                          };
                        } = {};
                        daysToShow.slice(0, 7).forEach(date => {
                          const dateKey = formatDateKey(date);
                          const currentAvailability = getTruckAvailability(truck, date);
                          // Only update if not already assigned to another partner
                          if (!currentAvailability.partnerId || currentAvailability.partnerId === partner.id) {
                            updates[dateKey] = {
                              available: true,
                              partnerId: partner.id
                            };
                          }
                        });
                        updateTruckAvailability(truck.id, updates);
                      }} disabled={truck.status !== 'available'} className="text-xs text-blue-600 hover:text-blue-800 disabled:opacity-50 disabled:cursor-not-allowed px-2 py-1 rounded hover:bg-blue-50" title="Assign to all days" data-id="element-4080">
                                  Assign All
                                </button>
                                <button onClick={() => {
                        // Remove this truck from this partner for all visible days
                        const updates: {
                          [date: string]: {
                            available: boolean;
                            partnerId?: string;
                          };
                        } = {};
                        daysToShow.slice(0, 7).forEach(date => {
                          const dateKey = formatDateKey(date);
                          const currentAvailability = getTruckAvailability(truck, date);
                          // Only update if assigned to this partner
                          if (currentAvailability.partnerId === partner.id) {
                            updates[dateKey] = {
                              available: false,
                              partnerId: undefined
                            };
                          }
                        });
                        updateTruckAvailability(truck.id, updates);
                      }} disabled={truck.status !== 'available'} className="text-xs text-red-600 hover:text-red-800 disabled:opacity-50 disabled:cursor-not-allowed px-2 py-1 rounded hover:bg-red-50" title="Remove from all days" data-id="element-4081">
                                  Remove All
                                </button>
                              </div>
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>)}
        </div>}

      {trucks.length === 0 && <div className="text-center py-8" data-id="element-4082">
          <TruckIcon size={40} className="mx-auto text-gray-400 mb-2" data-id="element-4083" />
          <p className="text-gray-600" data-id="element-4084">No trucks in your fleet</p>
          <p className="text-sm text-gray-500 mt-1" data-id="element-4085">
            Add trucks to start managing availability
          </p>
        </div>}
    </div>;
};